import type { AxiosError } from "axios"
import { readonly, ref } from "vue"
import api from "./client"

interface SessionUser {
        id: number
        email: string
        avatarId: string
}

const user = ref<SessionUser | null>(null)
const error = ref("")
let pending: Promise<void> | null = null
let isEndingSession = false

export const sessionUser = readonly(user)
export const sessionError = readonly(error)

function clearSession() {
        user.value = null
        error.value = ""
}

export function refreshSession(): Promise<void> {
        if (isEndingSession) return Promise.resolve()
        if (pending) return pending.catch(() => {})
        pending = (async () => {
                try {
                        const { data } = await api.get<SessionUser | null>("auth/session")
                        user.value = data
                        error.value = ""
                } catch (reason) {
                        if ((reason as AxiosError).response?.status === 401) {
                                clearSession()
                                return
                        }
                        error.value = "Не удалось проверить вход. Проверьте соединение и попробуйте ещё раз."
                }
        })().finally(() => { pending = null })
        return pending
}

export async function confirmLoginSession(email: string): Promise<void> {
        // Дожидаемся предыдущего запроса перед подтверждением нового входа.
        if (pending) await pending.catch(() => {})
        await refreshSession()
        if (error.value) throw new Error(error.value)
        if (!user.value || user.value.email.toLowerCase() !== email.toLowerCase()) {
                throw new Error("Не удалось подтвердить вход. Попробуйте войти ещё раз.")
        }
}

export async function saveAccountAvatar(avatarId: string): Promise<void> {
        while (pending) await pending.catch(() => {})
        if (isEndingSession || !user.value) throw new Error("Сессия завершена. Войдите в аккаунт снова.")

        pending = (async () => {
                try {
                        const { data } = await api.patch<SessionUser>("auth/account/avatar", { avatarId })
                        user.value = data
                        error.value = ""
                } catch (reason) {
                        if ((reason as AxiosError).response?.status === 401) {
                                clearSession()
                                throw new Error("Сессия завершена. Войдите в аккаунт снова.")
                        }
                        throw new Error("Не удалось сохранить аватар. Проверьте соединение и попробуйте ещё раз.")
                }
        })().finally(() => { pending = null })
        return pending
}

async function endSession(endpoint: string, method: "POST" | "DELETE", message: string): Promise<void> {
        if (isEndingSession) return
        isEndingSession = true
        try {
                // Завершаем текущий запрос; новые запросы не восстановят сессию во время выхода.
                if (pending) await pending.catch(() => {})
                const { data } = await api.request<boolean>({ url: endpoint, method })
                if (method === "DELETE" && data !== true) throw new Error(message)
                clearSession()
        } catch (reason) {
                if ((reason as AxiosError).response?.status !== 401) throw new Error(message)
                clearSession()
        } finally {
                isEndingSession = false
        }
}

export const logoutSession = () => endSession("logout", "POST", "Не удалось выйти из аккаунта. Проверьте соединение и попробуйте ещё раз.")
export const deleteSessionAccount = () => endSession("auth/account", "DELETE", "Не удалось удалить аккаунт. Проверьте соединение и попробуйте ещё раз.")
